import { useState, useEffect, useRef } from 'react';
import {
  Modal,
  Box,
  Typography,
  TextField,
  Button,
  CircularProgress,
  IconButton,
  Alert,
  Paper,
  InputAdornment,
} from '@mui/material';
import { Close as CloseIcon, LocationOn as LocationIcon, MyLocation as MyLocationIcon } from '@mui/icons-material';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import { branchService } from '../../services/branchService';
import toast from 'react-hot-toast';

// Geocoding services configuration
const GEOCODING_SERVICES = [
  {
    name: 'Nominatim',
    searchUrl: (query) => `https://nominatim.openstreetmap.org/search?format=json&addressdetails=1&limit=5&q=${encodeURIComponent(query)}`,
    reverseUrl: (lat, lng) => `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&addressdetails=1`,
    parseSearch: (data) => data.map(item => ({
      display_name: item.display_name,
      lat: item.lat,
      lon: item.lon,
    })),
    parseReverse: (data) => ({
      display_name: data.display_name,
      address: data.address,
    }),
  },
  {
    name: 'GeocodeXYZ',
    searchUrl: (query) => `https://geocode.xyz/${encodeURIComponent(query)}?json=1`,
    reverseUrl: (lat, lng) => `https://geocode.xyz/${lat},${lng}?json=1`,
    parseSearch: (data) => {
      if (data.error) return [];
      return [{
        display_name: `${data.standard?.city || ''}, ${data.standard?.prov || ''}`.trim(),
        lat: data.latt,
        lon: data.longt,
      }];
    },
    parseReverse: (data) => ({
      display_name: data.standard?.city ? `${data.standard.city}, ${data.standard.prov || ''}` : 'Unknown location',
      address: data.standard,
    }),
  },
];

// Helper function for API calls with retry and rate limiting
const fetchWithRetry = async (url, options, maxRetries = 3) => {
  // Check if online
  if (!navigator.onLine) {
    throw new Error('No internet connection detected.');
  }

  // Rate limiting: minimum 1 second between requests
  const now = Date.now();
  if (fetchWithRetry.lastRequest && now - fetchWithRetry.lastRequest < 1000) {
    await new Promise(resolve => setTimeout(resolve, 1000 - (now - fetchWithRetry.lastRequest)));
  }
  fetchWithRetry.lastRequest = Date.now();

  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 10000);

      const response = await fetch(url, {
        ...options,
        signal: controller.signal,
      });

      clearTimeout(timeoutId);
      return response;
    } catch (err) {
      console.warn(`API call attempt ${attempt} failed:`, err.message);

      if (attempt === maxRetries) {
        throw err;
      }

      // Exponential backoff: 1s, 2s, 4s
      const delay = Math.pow(2, attempt - 1) * 1000;
      await new Promise(resolve => setTimeout(resolve, delay));
    }
  }
};

const style = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: { xs: '95%', sm: '90%', md: '75%' },
  maxHeight: '90vh',
  bgcolor: 'background.paper',
  borderRadius: 2,
  boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
  p: { xs: 1.5, sm: 2, md: 2.5 },
  overflow: 'auto',
};

export const BranchCreatePopup = ({ open, onClose, onSuccess, editingBranch }) => {
  const mapContainerRef = useRef(null);
  const mapRef = useRef(null);
  const markerRef = useRef(null);

  const [mapLoading, setMapLoading] = useState(false);
  const [message, setMessage] = useState(editingBranch ? 'Opening branch editor...' : 'Opening branch creation...');
  const [searchValue, setSearchValue] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState(null);
  
  const [formData, setFormData] = useState({
    name: editingBranch?.name || '',
    email: editingBranch?.email || '',
    password: editingBranch?.password || '',
    phone: editingBranch?.phone || '',
    address: editingBranch?.address || '',
    city: editingBranch?.city || '',
    state: editingBranch?.state || '',
    latitude: editingBranch?.latitude || '',
    longitude: editingBranch?.longitude || '',
  });
  const [submitting, setSubmitting] = useState(false);

  // Reverse geocode using multiple services with fallback
  const reverseGeocode = async (lat, lng) => {
    if (lat === undefined || lng === undefined || Number.isNaN(lat) || Number.isNaN(lng)) {
      setMessage('Invalid coordinates for reverse geocoding.');
      return null;
    }

    for (const service of GEOCODING_SERVICES) {
      try {
        console.log(`Trying ${service.name} for reverse geocoding...`);
        const endpoint = service.reverseUrl(lat, lng);
        const response = await fetchWithRetry(endpoint, {
          method: 'GET',
          headers: {
            'Accept': 'application/json',
            'Accept-Language': 'en-US,en;q=0.9',
            'Referer': window.location.origin,
            'User-Agent': 'LyraPOS/1.0 (demo@lyrapos.com)',
            'Cache-Control': 'no-cache',
          },
          mode: 'cors',
          cache: 'no-cache',
        });

        if (!response.ok) {
          if (response.status === 429) {
            console.warn(`${service.name} reverse rate limited (429)`);
            setMessage(`Rate limit exceeded for ${service.name}. Please wait and try again.`);
            return null;
          }
          console.warn(`${service.name} reverse returned ${response.status}`);
          continue;
        }

        const data = await response.json();
        const result = service.parseReverse(data);

        if (result && result.display_name) {
          const address = result.display_name;
          const city = result.address?.city || result.address?.town || result.address?.village || '';
          const state = result.address?.state || result.address?.region || result.address?.prov || '';

          setFormData((prev) => ({
            ...prev,
            address: address.substring(0, 255),
            city,
            state,
          }));

          setMessage('Address updated from map location.');
          return data;
        }
      } catch (err) {
        console.warn(`${service.name} reverse failed:`, err.message);
      }
    }

    // All services failed
    setMessage('Unable to fetch address from all services. Please enter manually.');
    return null;
  };

  // Search address using Nominatim
  const handleAddressSearch = async (value) => {
    setSearchValue(value);
    if (typeof value !== 'string' || value.trim().length < 3) {
      setSuggestions([]);
      return;
    }

    const query = value.trim() + ', India';
    setSearchLoading(true);

    for (const service of GEOCODING_SERVICES) {
      try {
        console.log(`Trying ${service.name} for search...`);
        const response = await fetchWithRetry(service.searchUrl(query), {
          method: 'GET',
          headers: {
            'Accept': 'application/json',
            'Accept-Language': 'en-US,en;q=0.9',
            'Referer': window.location.origin,
            'User-Agent': 'LyraPOS/1.0 (demo@lyrapos.com)',
            'Cache-Control': 'no-cache',
          },
          mode: 'cors',
          cache: 'no-cache',
        });

        if (!response.ok) {
          if (response.status === 429) {
            console.warn(`${service.name} rate limited (429)`);
            toast.error(`Rate limit exceeded for ${service.name}. Please wait a moment and try again.`);
            setSuggestions([]);
            setSearchLoading(false);
            return; // Don't try other services for rate limit
          }
          console.warn(`${service.name} returned ${response.status}`);
          continue; // Try next service for other errors
        }

        const data = await response.json();
        const suggestions = service.parseSearch(data);

        if (suggestions.length > 0) {
          setSuggestions(suggestions);
          setSearchLoading(false);
          return;
        }
      } catch (err) {
        console.warn(`${service.name} failed:`, err.message);
        // Continue to next service
      }
    }

    // All services failed
    console.error('All geocoding services failed');
    toast.error('Unable to search locations. All services are currently unavailable. Please try again later.');
    setSuggestions([]);
    setSearchLoading(false);
  };

  const handleSuggestionClick = (suggestion) => {
    const lat = parseFloat(suggestion.lat);
    const lng = parseFloat(suggestion.lon);

    setFormData(prev => ({
      ...prev,
      address: suggestion.display_name.substring(0, 255),
      latitude: lat.toFixed(6),
      longitude: lng.toFixed(6),
      city: '',
      state: '',
    }));
    setSelectedLocation({ lat, lng });

    // Update marker
    if (markerRef.current) {
      markerRef.current.setLatLng([lat, lng]);
    } else {
      markerRef.current = L.marker([lat, lng], { draggable: true })
        .addTo(mapRef.current)
        .bindPopup('Drag marker to adjust location')
        .openPopup();

      markerRef.current.on('dragend', () => {
        const p = markerRef.current.getLatLng();
        handleMapClick(p.lat, p.lng);
      });
    }

    mapRef.current.setView([lat, lng], 15);
    reverseGeocode(lat, lng);
    setSuggestions([]);
    setSearchValue('');
    toast.success('Location updated from search');
  };

  const handleMapClick = async (lat, lng) => {
    const roundedLat = parseFloat(lat.toFixed(6));
    const roundedLng = parseFloat(lng.toFixed(6));

    if (!mapRef.current) {
      setMessage('Map is not initialized yet.');
      return;
    }

    if (markerRef.current) {
      markerRef.current.setLatLng([roundedLat, roundedLng]);
    } else {
      markerRef.current = L.marker([roundedLat, roundedLng], { draggable: true })
        .addTo(mapRef.current)
        .bindPopup('Drag marker to adjust location')
        .openPopup();

      markerRef.current.on('dragend', async () => {
        const p = markerRef.current.getLatLng();
        await handleMapClick(p.lat, p.lng);
      });
    }

    mapRef.current.setView([roundedLat, roundedLng], 13);

    setFormData((prev) => ({
      ...prev,
      latitude: roundedLat.toString(),
      longitude: roundedLng.toString(),
    }));
    setSelectedLocation({ lat: roundedLat, lng: roundedLng });

    setMessage('Fetching address for selected location...');
    const geoData = await reverseGeocode(roundedLat, roundedLng);

    if (geoData) {
      setMessage('Address updated from selected location.');
    } else {
      // Keep existing city/state/address if reverse failed
      setMessage('Could not fetch exact address, please verify or type manually.');
    }
  };

  const initMap = async (center) => {
    if (!mapContainerRef.current) return;

    if (mapRef.current) {
      mapRef.current.off();
      mapRef.current.remove();
      mapRef.current = null;
      markerRef.current = null;
    }

    setMapLoading(true);

    try {
      mapRef.current = L.map(mapContainerRef.current, {
        center: [center.lat, center.lng],
        zoom: 13,
        scrollWheelZoom: true,
        zoomControl: true,
      });

      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: '&copy; OpenStreetMap contributors',
        maxZoom: 19,
      }).addTo(mapRef.current);

      mapRef.current.on('click', (e) => {
        handleMapClick(e.latlng.lat, e.latlng.lng);
      });

      // Ensure leaflet knows its container size especially in modal context.
      mapRef.current.whenReady(() => {
        setTimeout(() => {
          mapRef.current.invalidateSize();
          mapRef.current.setView([center.lat, center.lng], 13);
        }, 100);
      });

      // Place marker for default location or existing branch coordinates.
      if (center) {
        handleMapClick(center.lat, center.lng);
      }

      setMapLoading(false);
    } catch (err) {
      console.error('Map initialization error:', err);
      setMapLoading(false);
      toast.error('Failed to initialize map');
    }
  };

  useEffect(() => {
    if (!open) return;

    const setupFormData = () => {
      if (editingBranch) {
        setFormData({
          name: editingBranch.name || '',
          email: editingBranch.email || '',
          password: '',
          phone: editingBranch.phone || '',
          address: editingBranch.address || '',
          city: editingBranch.city || '',
          state: editingBranch.state || '',
          latitude: editingBranch.latitude || '',
          longitude: editingBranch.longitude || '',
        });

        if (editingBranch.latitude && editingBranch.longitude) {
          setSelectedLocation({
            lat: Number(editingBranch.latitude),
            lng: Number(editingBranch.longitude),
          });

          // If address is empty but coordinates exist, try reverse geocoding
          if (!editingBranch.address && editingBranch.latitude && editingBranch.longitude) {
            setMessage('Loading address from coordinates...');
            reverseGeocode(Number(editingBranch.latitude), Number(editingBranch.longitude)).then((geoData) => {
              if (geoData) {
                setMessage('Address loaded from existing coordinates.');
              } else {
                setMessage('Could not load address from coordinates; please enter manually.');
              }
            });
          }
        }

        setMessage('✓ Editing existing branch; data has been pre-filled.');
        return;
      }

      setFormData({
        name: '',
        email: '',
        password: '',
        phone: '',
        address: '',
        city: '',
        state: '',
        latitude: '',
        longitude: '',
      });
      setSelectedLocation(null);
      setMessage('Opening branch creation...');
    };

    setupFormData();

    const defaultLocation = { lat: 11.744699, lng: 79.768021 };
    let activeCenter = defaultLocation;

    if (editingBranch && editingBranch.latitude && editingBranch.longitude) {
      activeCenter = {
        lat: Number(editingBranch.latitude),
        lng: Number(editingBranch.longitude),
      };
      setMessage('✓ Branch location loaded. You can update the location on the map.');
    }

    if (!navigator.geolocation || (editingBranch && editingBranch.latitude && editingBranch.longitude)) {
      setTimeout(() => initMap(activeCenter), 50);
      return;
    }

    setMessage('📍 Requesting location permission...');
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        activeCenter = {
          lat: Number(pos.coords.latitude.toFixed(6)),
          lng: Number(pos.coords.longitude.toFixed(6)),
        };
        setMessage('✓ Location permission granted. Map centered on your location.');
        setTimeout(() => initMap(activeCenter), 50);
      },
      (err) => {
        let errorMsg = 'Unknown error';
        switch (err.code) {
          case err.PERMISSION_DENIED:
            errorMsg = 'Location permission was denied.';
            break;
          case err.POSITION_UNAVAILABLE:
            errorMsg = 'Location information is unavailable.';
            break;
          case err.TIMEOUT:
            errorMsg = 'Location request timed out.';
            break;
          default:
            errorMsg = 'Failed to get location.';
        }
        setMessage(`⚠️ ${errorMsg} Using default location (Cuddalore).`);
        setTimeout(() => initMap(defaultLocation), 50);
      },
      {
        enableHighAccuracy: true,
        timeout: 5000,
        maximumAge: 60000,
      }
    );

    // Cleanup on modal close or editingBranch change
    return () => {
      if (mapRef.current) {
        mapRef.current.off();
        mapRef.current.remove();
        mapRef.current = null;
      }
      markerRef.current = null;
      setMapLoading(false);
      setMessage(editingBranch ? 'Opening branch editor...' : 'Opening branch creation...');
      setSearchValue('');
      setSuggestions([]);
    };
  }, [open, editingBranch]);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!formData.name || !formData.address || !formData.latitude || !formData.longitude) {
      toast.error('Please fill in branch name, address, and select a location on the map.');
      return;
    }

    setSubmitting(true);
    try {
      let response;
      if (editingBranch) {
        // Update existing branch
        response = await branchService.updateBranch(editingBranch.id, formData);
        toast.success('Branch updated successfully');
      } else {
        // Create new branch
        response = await branchService.createBranch(formData);
        toast.success('Branch created successfully');
      }
      onSuccess?.(response.data);
      onClose();
    } catch (error) {
      const message = error.response?.data?.message || (editingBranch ? 'Failed to update branch' : 'Failed to create branch');
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose}>
      <Box sx={style}>
        {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2.5, gap: 2 }}>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5 }}>
              {editingBranch ? 'Edit Branch' : 'Create Branch'}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
              {message}
            </Typography>
          </Box>
          <IconButton onClick={onClose} size="small" disabled={submitting} sx={{ flexShrink: 0 }}>
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Map Container */}
        <Box
          ref={mapContainerRef}
          sx={{
            width: '100%',
            minHeight: '250px',
            height: { xs: '250px', sm: '300px', md: '350px' },
            borderRadius: 2,
            overflow: 'hidden',
            mb: 3,
            position: 'relative',
            border: '1px solid #e0e0e0',
          }}
        >
          {mapLoading && (
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                backgroundColor: 'rgba(255,255,255,0.9)',
                zIndex: 10,
              }}
            >
              <CircularProgress />
            </Box>
          )}
        </Box>

        {/* Address Search */}
        <Box sx={{ mb: 3, position: 'relative', zIndex: 100 }}>
          <TextField
            fullWidth
            label="Search Location"
            placeholder="Type address to search (India)..."
            value={searchValue}
            onChange={(e) => handleAddressSearch(e.target.value)}
            disabled={submitting}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  {searchLoading ? <CircularProgress size={20} /> : <LocationIcon />}
                </InputAdornment>
              ),
            }}
            size="small"
          />
          {suggestions.length > 0 && (
            <Paper
              sx={{
                position: 'absolute',
                top: 'calc(100% + 4px)',
                left: 0,
                right: 0,
                maxHeight: '250px',
                overflowY: 'auto',
                zIndex: 1000,
              }}
            >
              {suggestions.map((suggestion, idx) => (
                <Box
                  key={idx}
                  sx={{
                    p: 1,
                    borderBottom: idx < suggestions.length - 1 ? '1px solid #eee' : 'none',
                    cursor: 'pointer',
                    '&:hover': { backgroundColor: '#f5f5f5' },
                  }}
                  onClick={() => handleSuggestionClick(suggestion)}
                >
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {suggestion.display_name.split(',').slice(0, 3).join(',')}
                  </Typography>
                </Box>
              ))}
            </Paper>
          )}
        </Box>

        {/* Selected Location Info */}
        {selectedLocation && (
          <Alert severity="success" sx={{ mb: 3 }} icon={<LocationIcon sx={{ color: 'success.main' }} />}>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              Location Selected
            </Typography>
            <Typography variant="caption" sx={{ display: 'block', mt: 0.5 }}>
              {selectedLocation.lat.toFixed(6)} • {selectedLocation.lng.toFixed(6)}
            </Typography>
          </Alert>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit}>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1.5, mb: 2.5 }}>
            {/* Branch Name */}
            <TextField
              label="Branch Name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              required
              fullWidth
              disabled={submitting}
              size="small"
              placeholder="Main Street Branch"
            />

            {/* Email */}
            <TextField
              label="Email"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleChange}
              fullWidth
              disabled={submitting}
              size="small"
              placeholder="branch@example.com"
            />

            {/* Password */}
            <TextField
              label="Password"
              name="password"
              type="password"
              value={formData.password}
              onChange={handleChange}
              fullWidth
              disabled={submitting}
              size="small"
              placeholder="Min 6 characters"
            />

            {/* Phone */}
            <TextField
              label="Phone"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              fullWidth
              disabled={submitting}
              size="small"
              placeholder="10 digit number"
            />

            {/* Address (Full Width) */}
            <TextField
              label="Address"
              name="address"
              value={formData.address}
              onChange={handleChange}
              required
              fullWidth
              disabled={submitting}
              size="small"
              placeholder="Complete street address"
              sx={{ gridColumn: { xs: 'span 1', sm: 'span 2' } }}
            />

            {/* City */}
            <TextField
              label="City"
              name="city"
              value={formData.city}
              onChange={handleChange}
              fullWidth
              disabled={submitting}
              size="small"
            />

            {/* State */}
            <TextField
              label="State"
              name="state"
              value={formData.state}
              onChange={handleChange}
              fullWidth
              disabled={submitting}
              size="small"
            />

            {/* Latitude (Read-only) */}
            <TextField
              label="Latitude"
              name="latitude"
              value={formData.latitude}
              inputProps={{ readOnly: true }}
              fullWidth
              disabled={submitting}
              size="small"
            />

            {/* Longitude (Read-only) */}
            <TextField
              label="Longitude"
              name="longitude"
              value={formData.longitude}
              inputProps={{ readOnly: true }}
              fullWidth
              disabled={submitting}
              size="small"
            />
          </Box>

          {/* Form Actions */}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, pt: 2, borderTop: '1px solid #e0e0e0' }}>
            <Button
              variant="outlined"
              onClick={onClose}
              disabled={submitting}
              size="small"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={submitting || !formData.latitude || !formData.longitude}
              startIcon={submitting ? <CircularProgress size={18} /> : undefined}
              size="small"
            >
              {submitting
                ? editingBranch
                  ? 'Updating...'
                  : 'Creating...'
                : editingBranch
                  ? 'Update Branch'
                  : 'Create Branch'}
            </Button>
          </Box>
        </form>
      </Box>
    </Modal>
  );
};
